import type { PricePlanRow } from "../../../services/PricePlanService";

/**
 * Hàm thuần cho màn Price plans — tách riêng để unit test không cần render.
 */

/** Đọc nội dung file .json: chấp nhận `{ plan: {...} }` hoặc plan trả thẳng. */
export function parsePlanFileText(text: string): { plan: unknown } | { error: string } {
	let parsed: any;
	try {
		parsed = JSON.parse(text);
	} catch (_e) {
		return { error: "File is not valid JSON." };
	}
	if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
		return { error: "File must contain a JSON object." };
	}
	const plan = parsed.plan && typeof parsed.plan === "object" ? parsed.plan : parsed;
	if (!Array.isArray(plan.rows)) {
		return { error: 'Plan has no "rows" array.' };
	}
	return { plan };
}

export type SortKey = "station" | "group" | "currentStandard" | "nextStandard" | "changePct";
export type SortDir = "asc" | "desc";
export type ChangeFilter = "all" | "up" | "down" | "same" | "big";

export const BIG_CHANGE_PCT = 40;

export interface RowFilter {
	search: string;
	country: string;
	group: string;
	change: ChangeFilter;
}

export const EMPTY_FILTER: RowFilter = { search: "", country: "", group: "", change: "all" };

const norm = (s: unknown) => String(s ?? "").toLowerCase();

export function rowMatches(row: PricePlanRow, f: RowFilter): boolean {
	if (f.country && (row.country || "") !== f.country) return false;
	if (f.group && (row.group || "") !== f.group) return false;
	const pct = row.changePct ?? 0;
	if (f.change === "up" && !(pct > 0)) return false;
	if (f.change === "down" && !(pct < 0)) return false;
	if (f.change === "same" && pct !== 0) return false;
	if (f.change === "big" && !(Math.abs(pct) > BIG_CHANGE_PCT)) return false;
	const q = norm(f.search).trim();
	if (q) {
		const hay = [row.partnerId, row.partnerName, row.city, row.zoneName, row.note].map(norm).join(" ");
		if (hay.indexOf(q) < 0) return false;
	}
	return true;
}

function sortValue(row: PricePlanRow, key: SortKey): string | number {
	switch (key) {
		case "station":
			return norm(row.partnerName) || String(row.partnerId);
		case "group":
			return norm(row.group);
		case "currentStandard":
			return row.current?.standard ?? -Infinity;
		case "nextStandard":
			return row.next?.standard ?? -Infinity;
		case "changePct":
			return row.changePct ?? 0;
	}
}

export function compareRows(a: PricePlanRow, b: PricePlanRow, key: SortKey, dir: SortDir): number {
	const va = sortValue(a, key);
	const vb = sortValue(b, key);
	let c = 0;
	if (typeof va === "number" && typeof vb === "number") c = va - vb;
	else c = String(va).localeCompare(String(vb), "en");
	if (c === 0) c = Number(a.partnerId) - Number(b.partnerId);
	return dir === "asc" ? c : -c;
}

export interface ZoneGroup {
	key: string;
	zoneName: string;
	rows: PricePlanRow[];
}
export interface CityGroup {
	key: string;
	city: string;
	zones: ZoneGroup[];
	count: number;
}
export interface CountryGroup {
	key: string;
	country: string;
	cities: CityGroup[];
	count: number;
}

/** Nhóm country → city → zone (giữ thứ tự sort bên trong mỗi zone). */
export function groupRows(rows: PricePlanRow[], key: SortKey, dir: SortDir): CountryGroup[] {
	const sorted = rows.slice().sort((a, b) => compareRows(a, b, key, dir));
	const countries: CountryGroup[] = [];
	const cMap: { [k: string]: CountryGroup } = {};
	for (const row of sorted) {
		const country = row.country || "Unknown country";
		const city = row.city || "Unknown city";
		const zoneName = row.zoneName || (row.zoneId != null ? `Zone ${row.zoneId}` : "No zone");
		let cg = cMap[country];
		if (!cg) {
			cg = { key: country, country, cities: [], count: 0 };
			cMap[country] = cg;
			countries.push(cg);
		}
		cg.count += 1;
		let ci = cg.cities.find((x) => x.city === city);
		if (!ci) {
			ci = { key: `${country}|${city}`, city, zones: [], count: 0 };
			cg.cities.push(ci);
		}
		ci.count += 1;
		let z = ci.zones.find((x) => x.zoneName === zoneName);
		if (!z) {
			z = { key: `${country}|${city}|${zoneName}`, zoneName, rows: [] };
			ci.zones.push(z);
		}
		z.rows.push(row);
	}
	const byName = (a: { key: string }, b: { key: string }) => a.key.localeCompare(b.key, "en");
	countries.sort(byName);
	countries.forEach((c) => {
		c.cities.sort(byName);
		c.cities.forEach((ci) => ci.zones.sort(byName));
	});
	return countries;
}

export function uniqueValues(rows: PricePlanRow[], pick: (r: PricePlanRow) => string | null | undefined): string[] {
	const seen: { [k: string]: true } = {};
	const out: string[] = [];
	rows.forEach((r) => {
		const v = pick(r);
		if (v && !seen[v]) {
			seen[v] = true;
			out.push(v);
		}
	});
	return out.sort((a, b) => a.localeCompare(b, "en"));
}

export const fmtUsd = (v: number | null | undefined): string =>
	v == null || !isFinite(Number(v)) ? "—" : `$${Number(v).toFixed(2)}`;

export const fmtPct = (v: number | null | undefined): string => {
	if (v == null || !isFinite(Number(v))) return "—";
	const n = Number(v);
	const s = n.toFixed(1);
	return n > 0 ? `+${s}%` : `${s}%`;
};

export const fmtDateTime = (v: string | null | undefined): string => {
	if (!v) return "—";
	const d = new Date(v);
	return isNaN(d.getTime()) ? String(v) : d.toLocaleString("en-GB");
};

export const fmtDuration = (ms: number | null | undefined): string => {
	if (ms == null || !isFinite(Number(ms))) return "—";
	return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;
};
